"use client";

import React from "react";
import { renderAsync } from "docx-preview";
import { Spinner } from "@heroui/react";

import { logger } from "@/telemetry";

export interface DocumentInfo {
  /** Word count */
  wordCount: number;
}

export interface DOCXViewerProps {
  /** DOCX document as ArrayBuffer */
  documentData: ArrayBuffer;
  /** Optional CSS class name for the container */
  className?: string;
  /** Optional inline styles for the container */
  style?: React.CSSProperties;
  /** Callback when document is rendered, providing the container element and extracted text */
  onRendered?: (container: HTMLDivElement, documentText: string) => void;
  /** Callback with document info (page count, word count) after rendering */
  onDocumentInfo?: (info: DocumentInfo) => void;
  /** Loading label text */
  loadingLabel?: string;
  /** Error message to display on failure */
  errorMessage?: string;
}

/**
 * Shared DOCX Viewer component using docx-preview
 * Renders DOCX files as HTML with loading and error states
 *
 * For citation highlighting, use the Citations/viewers/DOCXViewer which extends this component.
 */
export const DOCXViewer: React.FC<DOCXViewerProps> = ({
  documentData,
  className,
  style,
  onRendered,
  onDocumentInfo,
  loadingLabel = "Loading document...",
  errorMessage = "Failed to render document",
}) => {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  // Render DOCX document
  React.useEffect(() => {
    if (!documentData || !containerRef.current) {
      return;
    }

    let isMounted = true;

    const renderDOCX = async () => {
      const container = containerRef.current;

      if (!container) return;

      try {
        setLoading(true);
        setError(null);

        // Clear container
        container.innerHTML = "";

        // Render DOCX to HTML
        await renderAsync(documentData, container, undefined, {
          className: "docx-wrapper",
          inWrapper: true,
          ignoreWidth: true, // Allow content to adapt to container width
          ignoreHeight: true, // Allow content to flow naturally
          renderHeaders: true,
          renderFooters: true,
          renderFootnotes: true,
          renderEndnotes: true,
          experimental: true,
        });

        if (!isMounted) return;

        // Extract text content from rendered HTML
        const extractedText = container.innerText || container.textContent || "";

        // Calculate word count from extracted text
        const wordCount = extractedText
          .trim()
          .split(/\s+/)
          .filter((word) => word.length > 0).length;

        setLoading(false);

        // Notify parent with document info
        onDocumentInfo?.({ wordCount });

        // Notify parent component that rendering is complete
        onRendered?.(container, extractedText);
      } catch (err) {
        logger.error("Failed to render DOCX", err);
        if (isMounted) {
          setError(errorMessage);
          setLoading(false);
        }
      }
    };

    renderDOCX();

    return () => {
      isMounted = false;
    };
  }, [documentData, onRendered, onDocumentInfo, errorMessage]);

  return (
    <div className={className ?? "w-full h-[75vh] overflow-auto bg-content1 p-8"} style={style}>
      {/* Always render container so ref is available */}
      <div
        ref={containerRef}
        className="docx-container w-full max-w-full sm:max-w-4xl mx-auto"
        style={{
          /* DOCX preview styles */
          fontFamily: "Calibri, sans-serif",
          fontSize: "11pt",
          lineHeight: "1.5",
          display: loading || error ? "none" : "block",
          /* Ensure content doesn't overflow on mobile */
          overflowWrap: "break-word",
          wordWrap: "break-word",
        }}
      />

      {/* Loading overlay */}
      {loading && (
        <div className="flex items-center justify-center h-[60vh]">
          <Spinner label={loadingLabel} />
        </div>
      )}

      {/* Error overlay */}
      {error && (
        <div className="flex items-center justify-center h-[60vh]">
          <p className="text-danger-500">{error}</p>
        </div>
      )}
    </div>
  );
};

export default DOCXViewer;
